import { AUTH_LOGIN, AUTH_LOGOUT, AuthLoginAction } from './auth';
import { ChallengeResult, openChallengeDialog } from './challenge';
import { IStoreState, ThunkDispatch, ThunkResult } from '.';

import { Action } from 'redux';
import _ from 'lodash';
import axios from 'axios';

// Types

interface ApiState {
  force_workflow: string,
  requestArgs: RequestArgs
  forceWorkflow: string | null
}

interface AnalyzeResponse {
  actionCode: "ALLOW" | "DENY" | "CHALLENGE" | "REVIEW" | null
  soapResponse: any
}

export type RequestArgs = any

// Filters 

interface FilterArgs {
  // request structure from current app state (incl token, security header, user identification, event/custom facts)
  args: RequestArgs | null

  // runtime arguments, those passed explicitly by the client or generated in realtime
  rootRequest: RequestArgs

  // SOAP action
  action: string

  getState(): IStoreState
}

const Filters = {
  /* Filters are functions which are called at runtime with a FilterArgs object, should return the (modified?) argument structure */

  // include device request only when doing analyze, notify, challenge or updating/browsing devices
  deviceRequest: ({ args, action, rootRequest }: FilterArgs) => {
    return ["analyze", "notify"].includes(action) || ['UPDATE_DEVICE', 'BROWSE_DEVICES'].includes(_.get(rootRequest, 'deviceManagementRequest.actionTypeList.deviceActionTypes', '')) ? args : null
  },

  // generic filtering by SOAP action
  filterByAction: (...actions: string[]) => ({ args, action }: FilterArgs) => {
    return actions.includes(action) ? args : null
  },

  // includes only if not using a proxy (direct connection)
  noProxy: ({ args, getState }: FilterArgs) => {
    return getState().config.aa.mode !== "proxy" ? args : null;
  },

  forceWorkflowEnabled: ({ args, getState }: FilterArgs) => {
    return getState().api.forceWorkflow ? args : null
  },

  apply: (filterArgs: FilterArgs): any => {
    let { args } = filterArgs

    if (typeof args === "string")
      return args
    else if (typeof args === "function")
      return Filters.apply({ ...filterArgs, args: args(filterArgs) })
    else if (_.isArray(args))
      return _.compact(args.map(v => Filters.apply({ ...filterArgs, args: v })))
    else if (_.has(args, '$filter') || _.has(args, '$value')) {
      let v = _.has(args, '$value') ? args.$value : _.omit(args, '$filter')

      let fnStack = _.castArray(_.get(args, '$filter', []))
      return _.reduce(fnStack, (acc, cur) => cur({ ...filterArgs, args: acc }), Filters.apply({ ...filterArgs, args: v }))
    } else {
      return _.pickBy(_.mapValues(args, obj => Filters.apply({ ...filterArgs, args: obj })), _.identity)
    }
  }
}


// Initialization

const initialState: ApiState = {
  force_workflow: "",
  requestArgs: {
    deviceRequest: {
      $filter: Filters.deviceRequest,
      devicePrint: "",
      deviceTokenCookie: localStorage.getItem('deviceTokenCookie') || "",
      httpAccept: "",
      httpAcceptChars: "",
      httpAcceptEncoding: "",
      httpAcceptLanguage: "",
      httpReferrer: "",
      ipAddress: "",
      userAgent: "",
    },
    identificationData: {
      userName: "",
      sessionId: {
        $filter: Filters.filterByAction("authenticate", "challenge", "queryAuthStatus"),
        $value: "",
      },
      transactionId: {
        $filter: Filters.filterByAction("authenticate", "challenge", "queryAuthStatus"),
        $value: "",
      },
    },
    runRiskType: {
      $filter: Filters.filterByAction("analyze", "createUser", "updateUser"),
      $value: "ALL"
    },
    eventDataList: {
      $filter: Filters.filterByAction("analyze"),
      eventData: {
        clientDefinedAttributeList: {
          fact: [{
            name: "APPLICATION",
            value: "DEMOBANK",
            dataType: "STRING"
          }, {
            $filter: Filters.forceWorkflowEnabled,
            name: "FORCE WORKFLOW",
            value: {
              $value: ({ getState }: FilterArgs) => getState().api.forceWorkflow
            },
            dataType: "STRING"
          }
          ]
        }
      }
    },
    securityHeader: {
      method: "PASSWORD",
      callerId: {
        $filter: Filters.noProxy,
        $value: ({ getState }: FilterArgs) => getState().config.aa.callerId
      },
      callerCredential: {
        $filter: Filters.noProxy,
        $value: ({ getState }: FilterArgs) => getState().config.aa.callerCredential
      },
    }
  },
  forceWorkflow: null
}

const apiUrl = "/api"

let requestId = 0

// Action Definition

export const API_SETREQUESTARGS = 'api/SETREQUESTARGS'
export const API_SETFORCEWORKFLOW = 'api/SETFORCEWORKFLOW'

interface ApiSetRequestArgsAction extends Action {
  type: typeof API_SETREQUESTARGS,
  args: RequestArgs
}

interface ApiSetForceWorkflowAction extends Action {
  type: typeof API_SETFORCEWORKFLOW,
  value: string | null
}

// SOAP Request handling

export const analyze = (args: RequestArgs): ThunkResult<{ analyzePromise: Promise<AnalyzeResponse>, challengePromise: Promise<ChallengeResult> }> =>
  (dispatch, getState) => {
    if (getState().config.aa.mode === "bypass")
      return {
        analyzePromise: Promise.resolve({ actionCode: 'ALLOW', soapResponse: null } as AnalyzeResponse),
        challengePromise: Promise.resolve(ChallengeResult.None)
      }
    let challengeResolve: (value?: ChallengeResult) => void

    let challengePromise = new Promise<ChallengeResult>((resolve, reject) => {
      challengeResolve = resolve
    })

    // since we are doing a separate analyze call, clear out transactionId 
    dispatch(setRequestArgs({
      identificationData: {
        transactionId: "",
        sessionId: "",
      }
    }))

    let analyzePromise = dispatch(soap("analyze", args))
      .then(({ request, response }) => {
        try {
          // update the latest transactionId and sessionId
          dispatch(setRequestArgs({
            identificationData: {
              sessionId: response.identificationData.sessionId,
              transactionId: _.get(response, 'identificationData.transactionId', "")
            }
          }))

        } catch (err) {
          console.error(err);
        }

        // force action has been completed, clear our the force workflow
        dispatch(setForceWorkflow(null))

        let userStatus = ""
        try {
          userStatus = response.identificationData.userStatus
        } catch (err) {
          console.error(err)
          throw new Error("Invalid SOAP response")
        }

        if (userStatus === "LOCKOUT")
          throw new Error("Your account has been locked")
        else if (userStatus === "DELETE")
          throw new Error("Your account has been deleted")

        let actionCode = ""
        try {
          actionCode = response.riskResult.triggeredRule.actionCode
          if (actionCode === "CHALLENGE")
            dispatch(openChallengeDialog(request, response, challengeResolve))
          else
            challengeResolve(ChallengeResult.None)
        } catch (err) {
          console.error(err)
          challengeResolve(ChallengeResult.Error)
          throw new Error("Invalid SOAP response")
        }

        return {
          actionCode,
          soapResponse: response
        } as AnalyzeResponse
      })
      .catch(err => {
        challengeResolve(ChallengeResult.Error)
        throw err
      })

    return { analyzePromise, challengePromise }
  }

export const query = (args: RequestArgs) =>
  (dispatch: ThunkDispatch) =>
    dispatch(soap("query", args)).then(({ response }: { response: any }) => response)

export const queryAuthStatus = (args: RequestArgs) =>
  (dispatch: ThunkDispatch) =>
    dispatch(soap("queryAuthStatus", args)).then(({ response }: { response: any }) => response)

export const challenge = (args: RequestArgs) =>
  (dispatch: ThunkDispatch) =>
    dispatch(soap("challenge", args)).then(({ response }: { response: any }) => response)

export const createUser = (args: RequestArgs) =>
  (dispatch: ThunkDispatch) =>
    dispatch(soap("createUser", args)).then(({ response }: { response: any }) => response)

export const updateUser = (args: RequestArgs) =>
  (dispatch: ThunkDispatch) =>
    dispatch(soap("updateUser", args)).then(({ response }: { response: any }) => response)

export const notify = (args: RequestArgs): ThunkResult<Promise<any>> =>
  (dispatch, getState) => {
    if (getState().config.aa.mode === "bypass")
      return Promise.resolve({})
    return dispatch(soap("notify", args)).then(({ response }) => response)
  }

export const authenticate = (args: RequestArgs) =>
  (dispatch: ThunkDispatch) =>
    dispatch(soap("authenticate", args)).then(({ response }: { response: any }) => response)

function soap(soapaction: string, args: RequestArgs): ThunkResult<Promise<any>> {
  return (dispatch: ThunkDispatch, getState: (() => IStoreState)) => {
    if (!['direct', 'proxy'].includes(getState().config.aa.mode || ""))
      return Promise.reject("Cannot make SOAP request with application in " + (getState().config.aa.mode || "unknown") + " mode ")
    let runtimeArgs = _.merge({
      identificationData: {
        userName: getState().auth.userName || ""
      },
      messageHeader: {
        requestId: requestId++,
        requestType: soapaction.toUpperCase()
      },
    }, args)

    let state = getState()

    let stateRequestArgs = Filters.apply({ args: state.api.requestArgs, action: soapaction, rootRequest: runtimeArgs, getState })

    let requestArgs = _.merge({}, stateRequestArgs, runtimeArgs)

    return axios
      .post(apiUrl + "/soap/" + (state.config.aa.mode === "proxy" ? `proxy/${state.config.aa.proxyId}/` : 'direct/') + soapaction, requestArgs)
      .then(response => response.data)
      .then(data => {
        try {
          // always store the latest deviceTokenCookie
          dispatch(setRequestArgs({
            deviceRequest: {
              deviceTokenCookie: data.deviceResult.deviceData.deviceTokenCookie
            },
            identificationData: {
              sessionId: data.identificationData.sessionId || "",
            }
          }))

          localStorage.setItem("deviceTokenCookie", data.deviceResult.deviceData.deviceTokenCookie)
        } catch (err) {
          // Not really an error, AA didn't provide a new deviceTokenCookie
          console.info("No new deviceTokenCookie for %s SOAP action", soapaction);
        }
        return {
          request: requestArgs,
          response: data
        }
      })
  }
}

export const get = (endpoint: string) => {
  return axios.get(apiUrl + endpoint)
}

// ACTION HANDLERS

export const setRequestArgs = (args: RequestArgs): ApiSetRequestArgsAction => ({
  type: API_SETREQUESTARGS,
  args: args,
})

export const setForceWorkflow = (value: string | null): ApiSetForceWorkflowAction => ({
  type: API_SETFORCEWORKFLOW,
  value
})

export const initApi = () =>
  (dispatch: ThunkDispatch) =>
    axios.get(apiUrl + "/devicedata")
      .then(({ data }) => {
        // console.log(data)
        return dispatch(setRequestArgs({ deviceRequest: data }))
      })

// REDUCERS

export default (state = initialState, action: Action) => {
  switch (action.type) {
    case API_SETREQUESTARGS:
      return {
        ...state,
        requestArgs: _.mergeWith({}, state.requestArgs, (action as ApiSetRequestArgsAction).args, mergeRequestArgs)
      }
    case API_SETFORCEWORKFLOW:
      return {
        ...state,
        forceWorkflow: (action as ApiSetForceWorkflowAction).value
      }
    case AUTH_LOGIN:
      return {
        ...state,
        requestArgs: _.merge({}, state.requestArgs, { identificationData: { userName: (action as AuthLoginAction).userName } })
      }
    case AUTH_LOGOUT:
      return {
        ...state,
        requestArgs: _.merge({}, state.requestArgs, { identificationData: { userName: "" } })
      }
    default:
      return state
  }
}

const mergeRequestArgs = (objVal: any, srcVal: any): any => {
  // if original is anything but an object, replace with source
  if (typeof objVal !== "object")
    return srcVal

  if (_.isArray(objVal)) {
    if (_.isArray(srcVal))
      return _.concat(objVal, srcVal)
    else
      return srcVal
  }

  if (!_.has(srcVal, '$value') && !_.has(objVal, '$value'))
    return

  let filters = _.castArray(_.get(objVal, '$filter', [])).concat(_.castArray(_.get(srcVal, '$filter', [])))

  return {
    $value: (_.get(srcVal, '$value', srcVal)),
    ...(filters.length ? { $filter: filters } : {})
  }
}
